/**
 * Distance-unit resolution, formatting, and km/mile constants.
 *
 * Storage is always canonical km / meters. These helpers resolve the
 * *display* unit at render time, in priority order:
 *  1. The user's persisted setting (`UserSettings.distanceUnit`).
 *  2. The browser locale — en-US / en-GB / en-MM / en-LR speakers default
 *     to miles; everything else defaults to km.
 */

export type DistanceUnit = "km" | "mile";

/** Miles per km. The constant is named for the original call site —
 * `distanceKm * KM_PER_MILE` yields miles. 1 km ≈ 0.621371 mile. */
export const KM_PER_MILE = 0.621371;

const MILE_LOCALE_PREFIXES: ReadonlyArray<string> = [
  "en-us",
  "en-gb",
  "en-mm",
  "en-lr",
];

/**
 * Locale-driven default distance unit. Empty / unrecognised locales fall
 * through to "km" (the global majority). Pass `locale` explicitly in tests;
 * otherwise we read `navigator.language` when defined (skipped in SSR /
 * non-browser test contexts).
 */
export function localeDefaultDistanceUnit(locale?: string): DistanceUnit {
  const raw =
    locale ??
    (typeof navigator !== "undefined" ? navigator.language : undefined) ??
    "";
  const normalized = raw.toLowerCase();
  for (const prefix of MILE_LOCALE_PREFIXES) {
    if (normalized === prefix || normalized.startsWith(`${prefix},`)) {
      return "mile";
    }
  }
  return "km";
}

/**
 * Effective display distance unit. An explicit "km" / "mile" override wins;
 * null / undefined falls through to the locale default.
 */
export function resolveDistanceUnit(
  stored: DistanceUnit | null | undefined,
  locale?: string
): DistanceUnit {
  if (stored === "km" || stored === "mile") return stored;
  return localeDefaultDistanceUnit(locale);
}

export function distanceUnitLabel(unit: DistanceUnit): string {
  return unit === "mile" ? "mi" : "km";
}

export function formatDistanceValue(
  distanceM: number | null,
  unit: DistanceUnit
): string | null {
  if (distanceM == null || distanceM <= 0) return null;
  const km = distanceM / 1000;
  const value = unit === "mile" ? Math.round(km * KM_PER_MILE) : Math.round(km);
  return value.toLocaleString("en-US");
}

export function formatDistance(
  distanceM: number | null,
  unit: DistanceUnit
): string | null {
  const value = formatDistanceValue(distanceM, unit);
  if (value == null) return null;
  return `${value} ${distanceUnitLabel(unit)}`;
}
